import { useCallback, useEffect, useMemo, useRef, useState, type RefCallback } from 'react';

interface IncrementalListOptions {
  pageSize?: number;
  lookAhead?: number;
  resetKey: string;
}

export function useIncrementalList<T>(
  items: T[],
  { pageSize = 40, lookAhead = 10, resetKey }: IncrementalListOptions,
): { drawnItems: T[]; lookAheadIndex: number; lookAheadRef: RefCallback<HTMLElement> } {
  const hasIntersectionObserver = typeof IntersectionObserver !== 'undefined';
  const [drawnCount, setDrawnCount] = useState(pageSize);
  const [lastResetKey, setLastResetKey] = useState(resetKey);
  const observerRef = useRef<IntersectionObserver | null>(null);

  if (lastResetKey !== resetKey) {
    setLastResetKey(resetKey);
    setDrawnCount(pageSize);
  }

  useEffect(() => () => observerRef.current?.disconnect(), []);

  const lookAheadRef = useCallback<RefCallback<HTMLElement>>((node) => {
    observerRef.current?.disconnect();
    if (!node || !hasIntersectionObserver) return;
    observerRef.current ??= new IntersectionObserver(([entry]) => {
      if (entry?.isIntersecting) setDrawnCount((count) => count + pageSize);
    });
    observerRef.current.observe(node);
  }, [hasIntersectionObserver, pageSize]);

  const drawnItems = useMemo(
    () => (hasIntersectionObserver ? items.slice(0, drawnCount) : items),
    [drawnCount, hasIntersectionObserver, items],
  );

  return {
    drawnItems,
    lookAheadIndex: Math.max(0, drawnItems.length - lookAhead),
    lookAheadRef,
  };
}
